// Plan 98 — admin-facing documentation for each evaluator's config
// parameters. The evaluator's prose description lives in the DB
// (evaluators.description) so it can evolve without a deploy; parameter
// docs live here because they are coupled to the code's config_schema
// (device-data-api's zod mirror validates the same keys).

export interface ParamDoc {
  label: string;
  explain: string;
  unit?: string;
}

export const EVALUATOR_PARAM_DOCS: Record<string, Record<string, ParamDoc>> = {
  comms_no_connection: {
    min_severity: {
      label: 'Minimum severity',
      explain: 'Severity floor the moment a no_connection alert exists — even one day of silence scores at least this much.',
    },
    full_severity_days: {
      label: 'Full-severity days',
      unit: 'days',
      explain: 'Days of silence at which severity reaches 1.0. Severity ramps linearly: days_stale / this value.',
    },
  },
  sensor_flatline: {
    noise_floor_pa: {
      label: 'Noise floor',
      unit: 'Pa',
      explain: 'A day counts as flat when its full pressure range stays below this. Healthy fan cycling swings ~50 Pa+; a dead sensor sits under ~7 Pa.',
    },
    max_abs_median_pa: {
      label: 'Max |median|',
      unit: 'Pa',
      explain: 'The top cluster must also sit within this distance of ZERO absolute pressure. This is what separates a dead sensor (flat at ~0) from a healthy always-on fan (flat but at real pressure).',
    },
    min_runtime_minutes: {
      label: 'Min runtime',
      unit: 'min',
      explain: 'Only days with at least this much fan runtime count — no runtime means flat-at-zero is expected, not suspicious.',
    },
    min_days: {
      label: 'Min flat days',
      unit: 'days',
      explain: 'How many qualifying flat days within the window before the finding fires.',
    },
    window_days: {
      label: 'Window',
      unit: 'days',
      explain: 'Lookback window ending at the evaluated date.',
    },
  },
  clustering_degenerate: {
    max_cluster_count: {
      label: 'Max clusters',
      explain: 'A day is degenerate when clustering finds this many pressure levels or fewer (1 = fan on/off indistinguishable).',
    },
    min_days: {
      label: 'Min degenerate days',
      unit: 'days',
      explain: 'Qualifying days within the window before the finding fires.',
    },
    window_days: {
      label: 'Window',
      unit: 'days',
      explain: 'Lookback window ending at the evaluated date.',
    },
    min_runtime_minutes: {
      label: 'Min runtime',
      unit: 'min',
      explain: 'Only days with at least this much runtime count — an idle HVAC legitimately shows one cluster.',
    },
  },
  processing_failures: {
    min_days: {
      label: 'Min error days',
      unit: 'days',
      explain: 'Days with error_message set within the window before the finding fires.',
    },
    window_days: {
      label: 'Window',
      unit: 'days',
      explain: 'Lookback window ending at the evaluated date.',
    },
  },
  calibration_anchor_lost: {
    latch_step_pa: {
      label: 'Latch step',
      unit: 'Pa',
      explain: 'A single-day calibration-chain move this negative (or more) means the chain absorbed a negative transient as the new zero — fires immediately at high confidence.',
    },
    wedge_min_lowest_pa: {
      label: 'Wedge floor',
      unit: 'Pa',
      explain: 'The fan-off cluster reading at or above this (instead of ~0) marks a day as "stuck" for the wedge signature.',
    },
    wedge_min_days: {
      label: 'Min stuck days',
      unit: 'days',
      explain: 'Stuck days within the wedge window before the finding fires.',
    },
    wedge_window_days: {
      label: 'Wedge window',
      unit: 'days',
      explain: 'Lookback window for counting stuck days.',
    },
    near_zero_pa: {
      label: 'Near-zero band',
      unit: 'Pa',
      explain: 'Any day whose lowest cluster falls inside ±this of zero proves the anchor is alive — the finding stays quiet (continuous-fan homes hit this on their occasional zero-visits).',
    },
    near_zero_lookback_days: {
      label: 'Near-zero lookback',
      unit: 'days',
      explain: 'How far back to look for a near-zero day or any chain movement before declaring the anchor lost.',
    },
    min_off_minutes: {
      label: 'Min off-cluster minutes',
      unit: 'min',
      explain: 'The stuck cluster needs at least this much daily occupancy — a brief sliver is noise, not a stuck off-state.',
    },
  },
};
